import useUserBlockApi from '@/composables/userBlock/userBlockApi'
import { Dictionary } from '@/composables/advert/advertManager'
import { computed, reactive, ref, Ref } from 'vue'
import useConfiguration from '@/composables/configuration/configuration'
import { computeOffset } from '@/helper/pagination'
import { User } from '@/model/data-object/User'
import useUserBlockCache from '@/composables/userBlock/userBlockCache'
import { UserBlock } from '@/model/data-object/UserBlock'
import ApiError from '@/error/apiError'

const blockList: Dictionary<boolean> = reactive({})
const index: Ref<number> = ref(0)

export default function useUserBlock() {
  const { getBlockedUserIds } = useUserBlockApi()
  const {
    getBlockedUsersFromCache,
    flushBlockedUsersToCache,
  } = useUserBlockCache()

  const persistUserBlockCache = (blockedUserIds: Array<number>) => {
    blockedUserIds.forEach((blockedId: number) => (blockList[blockedId] = true))
    index.value++
  }

  const fetchBlockedUsers = async (user: User) => {
    const userBlockCache = getBlockedUsersFromCache()

    if (
      userBlockCache &&
      userBlockCache.userId === user.id &&
      userBlockCache.lastActivity ===
        user.activity.blockedUsersModifiedAt.getTime()
    ) {
      persistUserBlockCache(userBlockCache.blockedUserIds)
      return
    }

    const blockedUserIds = await getBlockedUserIds()
    persistUserBlockCache(blockedUserIds)
    flushBlockedUsersToCache(blockedUserIds)
  }

  const isUserBlocked = (userId: number) => {
    return Object.prototype.hasOwnProperty.call(blockList, userId)
  }

  return {
    fetchBlockedUsers,
    isUserBlocked,
  }
}

const hasNext = ref(true)
const loading = ref(false)
const totalCount = ref(0)
const bannedUsers: Ref<Array<UserBlock>> = ref([])
let page = 1

export function useUserBlockLoader() {
  const { getBlockedUsers } = useUserBlockApi()
  const { configuration } = useConfiguration()
  const loadedCount = computed(() => bannedUsers.value.length)

  const loadNext = async () => {
    const offset = computeOffset(page, configuration.profilePageSize)
    const { response, request, count } = getBlockedUsers(offset)
    loading.value = true
    await request()
    loading.value = false

    totalCount.value = count.value
    if (response.value) {
      response.value.forEach((user) => bannedUsers.value.push(user as UserBlock))
      if (response.value.length < configuration.profilePageSize) {
        hasNext.value = false
      }
    }

    page++
  }

  const removeBlockedUser = (userId: number) => {
    bannedUsers.value = bannedUsers.value.filter(
      (bannedUser: UserBlock) => bannedUser.blocked.id !== userId
    )
    index.value++
  }

  const clear = () => {
    page = 1
    totalCount.value = 0
    hasNext.value = true
    bannedUsers.value = []
  }

  return {
    loading,
    loadedCount,
    totalCount,
    bannedUsers,
    hasNext,
    loadNext,
    clear,
    removeBlockedUser,
  }
}

export function useProfileUserBlock(userId: number) {
  const isBlocking: Ref<boolean> = ref(false)
  const apiError: Ref<ApiError | undefined> = ref()
  const {
    blockUser: blockUserApi,
    unblockUser: unblockUserApi,
  } = useUserBlockApi()

  const isUserBlocked = computed(() => {
    const j = index.value // hack
    return Object.prototype.hasOwnProperty.call(blockList, userId)
  })

  const blockUser = async () => {
    isBlocking.value = true

    try {
      await blockUserApi(userId)
      blockList[userId] = true // todo if success
      index.value++
    } catch (e) {
      if (e instanceof ApiError) {
        apiError.value = e
      }
    }
    isBlocking.value = false
  }

  const unblockUser = async () => {
    isBlocking.value = true
    await unblockUserApi(userId)
    delete blockList[userId] // todo if success
    index.value++
    isBlocking.value = false
  }

  return {
    apiError,
    blockList,
    isBlocking,
    isUserBlocked,
    blockUser,
    unblockUser,
  }
}
